'use client';

import { Select as SelectPrimitive } from '@base-ui/react/select';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import type * as React from 'react';

const selectTriggerVariants = cva(
  'bg-field border-input rounded-lg border text-sm cursor-pointer ' +
    'inline-flex items-center justify-between gap-2 w-full min-w-0 outline-none transition-colors ' +
    'focus-visible:border-ring focus-visible:ring-ring/30 focus-visible:ring-2 ' +
    'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive ' +
    'data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:opacity-50 ' +
    'data-placeholder:text-muted-foreground',
  {
    variants: {
      size: {
        sm: 'h-7 px-2 text-xs',
        default: 'h-9 px-3 text-sm md:text-xs',
        lg: 'h-10 px-3 text-sm',
      },
    },
    defaultVariants: { size: 'default' },
  },
);

const selectPopupVariants = cva(
  'bg-popover text-popover-foreground rounded-lg border shadow-lg outline-none ' +
    'origin-[var(--transform-origin)] p-1 ' +
    'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 ' +
    'data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 ' +
    '[min-width:var(--anchor-width)] max-w-(--available-width) max-h-[min(var(--available-height),20rem)] overflow-y-auto',
  {
    variants: {
      size: {
        sm: 'text-xs',
        default: 'text-sm md:text-xs',
        lg: 'text-sm',
      },
    },
    defaultVariants: { size: 'default' },
  },
);

const Select = SelectPrimitive.Root;

type SelectTriggerProps = React.ComponentProps<typeof SelectPrimitive.Trigger> &
  VariantProps<typeof selectTriggerVariants>;

const SelectTrigger = ({ className, size = 'default', ...props }: SelectTriggerProps) => {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(selectTriggerVariants({ size, className }))}
      {...props}
    />
  );
};

const SelectValue = ({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) => {
  return <SelectPrimitive.Value data-slot="select-value" className={cn('truncate', className)} {...props} />;
};

const SelectIcon = ({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Icon>) => {
  return (
    <SelectPrimitive.Icon
      data-slot="select-icon"
      className={cn('shrink-0 text-muted-foreground', className)}
      {...props}
    >
      {children ?? <ChevronDown className="size-4" />}
    </SelectPrimitive.Icon>
  );
};

const SelectPortal = SelectPrimitive.Portal;

const SelectPositioner = ({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Positioner>) => {
  return (
    <SelectPrimitive.Positioner data-slot="select-positioner" className={cn('outline-none', className)} {...props} />
  );
};

type SelectPopupProps = React.ComponentProps<typeof SelectPrimitive.Popup> & VariantProps<typeof selectPopupVariants>;

// gloss
const SelectPopup = ({ className, size = 'default', children, ...props }: SelectPopupProps) => {
  return (
    <SelectPortal>
      {}
      <SelectPositioner sideOffset={4} alignItemWithTrigger={false} className="pointer-events-auto isolate z-50">
        <SelectPrimitive.Popup
          data-slot="select-popup"
          className={cn(selectPopupVariants({ size, className }))}
          {...props}
        >
          {children}
        </SelectPrimitive.Popup>
      </SelectPositioner>
    </SelectPortal>
  );
};

const SelectList = ({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.List>) => {
  return <SelectPrimitive.List data-slot="select-list" className={cn('outline-none', className)} {...props} />;
};

const SelectItem = ({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) => {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        'relative flex w-full min-w-0 cursor-pointer select-none items-start gap-2 rounded-md px-2 py-1.5 outline-none',
        'data-highlighted:bg-accent data-highlighted:text-accent-foreground',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
};

const SelectItemIndicator = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ItemIndicator>) => {
  return (
    <SelectPrimitive.ItemIndicator
      data-slot="select-item-indicator"
      className={cn('absolute inset-y-0 right-2 flex shrink-0 items-center justify-center', className)}
      {...props}
    >
      {children ?? <Check className="size-4" />}
    </SelectPrimitive.ItemIndicator>
  );
};

// gloss
const SelectItemText = ({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ItemText>) => {
  return <SelectPrimitive.ItemText data-slot="select-item-text" className={className} {...props} />;
};

const SelectGroup = ({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) => {
  return <SelectPrimitive.Group data-slot="select-group" className={className} {...props} />;
};

const SelectGroupLabel = ({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.GroupLabel>) => {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-group-label"
      className={cn('px-2 py-1.5 font-semibold text-muted-foreground text-xs', className)}
      {...props}
    />
  );
};

const SelectSeparator = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return <div data-slot="select-separator" className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />;
};

const SelectScrollUpArrow = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) => {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-arrow"
      className={cn('flex items-center justify-center py-1 text-muted-foreground', className)}
      {...props}
    >
      {children ?? <ChevronUp className="size-4" />}
    </SelectPrimitive.ScrollUpArrow>
  );
};

const SelectScrollDownArrow = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) => {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-arrow"
      className={cn('flex items-center justify-center py-1 text-muted-foreground', className)}
      {...props}
    >
      {children ?? <ChevronDown className="size-4" />}
    </SelectPrimitive.ScrollDownArrow>
  );
};

export {
  Select,
  SelectGroup,
  SelectGroupLabel,
  SelectIcon,
  SelectItem,
  SelectItemIndicator,
  SelectItemText,
  SelectList,
  SelectPopup,
  SelectPortal,
  SelectPositioner,
  SelectScrollDownArrow,
  SelectScrollUpArrow,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  selectPopupVariants,
  selectTriggerVariants,
};
